/**
 * Information about individual criterion applied to the variant
 * The criterion helps conclude the classification of the variant
 */
export interface ICriterion {
  id: number;
  code: string;
  description: string;
}

export interface IEvidencesPayload {
  id?: number;
  assertion: number;
  notes: string;
  sources: number[];
}

export interface IEvidence extends IEvidencesPayload {
  id: number;
}

export interface IClassificationCriteriaPayload {
  assertion: number;
  classification: number;
  criterion: {
    id: number;
  };
}

export interface IClassificationCriteria extends IClassificationCriteriaPayload {
  criterion: ICriterion;
  // TODO: Standardize types to use an enum [UV-3549]
  type: string;
  id: number;
}

// classification short name and name are basically the same except for VUS
export enum ClassificationShortName {
  PATHOGENIC = 'Pathogenic',
  LIKELY_PATHOGENIC = 'Likely Pathogenic',
  VUS = 'VUS',
  LIKELY_BENIGN = 'Likely Benign',
  BENIGN = 'Benign',
  SIGNIFICANT_RISK_ALLELE = 'Significant Risk Allele',
  INSIGNIFICANT_RISK_ALLELE = 'Insignificant Risk Allele',
}

export enum ClassificationName {
  PATHOGENIC = 'Pathogenic',
  LIKELY_PATHOGENIC = 'Likely Pathogenic',
  VUS = 'Variant of Uncertain Significance',
  LIKELY_BENIGN = 'Likely Benign',
  BENIGN = 'Benign',
  SIGNIFICANT_RISK_ALLELE = 'Significant Risk Allele',
  INSIGNIFICANT_RISK_ALLELE = 'Insignificant Risk Allele',
}

// Variant's classification as Pathogenic, Likely Pathogenic,
// Variant of Uncertain Significance, Likely Benign, Benign,
// Significant Risk Allele & Insignificant Risk Allele
// (Options come from the NCCN (https://www.nccn.org) system.)
export interface IClassification {
  id: number;
  name: ClassificationName;
  short_name: ClassificationShortName;
}

export interface IClassificationReview {
  id: number;
  notes: string;
}

export interface INomenclature {
  id?: number;
  variant: number;
  name: string;
  type: NomenclatureType;
  // is_primary indicates the primary way to display the variant
  // should only be one is_primary per variant
  is_primary: boolean;
}

// matches the Nomenclature.TYPES from Nomenclature mdoel
export enum NomenclatureType {
  // gHGVS: genomic DNA change Human Genome Variation Society
  GHGVS = 'gHGVS',
  // cHGVS: nucleic acid Human Genome Variation Society
  CHGVS = 'cHGVS',
  // pHGVS: protein Human Genome Variation Society
  PHGVS = 'pHGVS',
  BIC = 'BIC',
  EXON = 'Exon',
}

// list from variant_classifications/constants; we should keep these consistent with any backend changes to this enum
export enum Disorder {
  ARRHYTHMOGENIC_CARDIOMYOPATHY = 'arrhythmogenic cardiomyopathy',
  ARRHYTHMOGENIC_CARDIOMYOPATHY_AND_DILATED_CARDIOMYOPATHY = 'arrhythmogenic cardiomyopathy and dilated cardiomyopathy',
  BRUGADA_SYNDROME = 'Brugada syndrome',
  CATECHOLAMINERGIC_POLYMORPHIC_VENTRICULAR_TACHYCARDIA = 'catecholaminergic polymorphic ventricular tachycardia',
  DILATED_CARDIOMYOPATHY = 'dilated cardiomyopathy',
  FABRY_DISEASE = 'Fabry disease',
  FAMILIAL_ADENOMATOUS_POLYPOSIS = 'familial adenomatous polyposis',
  FAMILIAL_HYPERCHOLESTEROLEMIA = 'familial hypercholesterolemia',
  FAMILIAL_THORACIC_AORTIC_ANEURYSM_AND_AORTIC_DISSECTION = 'familial thoracic aortic aneurysm and aortic dissection',
  HEREDITARY_BREAST_AND_OVARIAN_CANCER_SYNDROME = 'hereditary breast and ovarian cancer syndrome',
  HYPERTROPHIC_CARDIOMYOPATHY = 'hypertrophic cardiomyopathy',
  JUVENILE_POLYPOSIS_SYNDROME = 'juvenile polyposis syndrome',
  LI_FRAUMENI_SYNDROME = 'Li-Fraumeni syndrome',
  LOEYS_DIETZ_SYNDROME = 'Loeys-Dietz syndrome',
  LONG_QT_SYNDROME = 'long QT syndrome',
  LYNCH_SYNDROME = 'Lynch syndrome',
  MALIGNANT_HYPERTHERMIA_SUSCEPTIBILITY = 'malignant hyperthermia susceptibility',
  MARFAN_SYNDROME = 'Marfan syndrome',
  MULTIPLE_ENDOCRINE_NEOPLASIA_TYPE_1 = 'multiple endocrine neoplasia type 1',
  MULTIPLE_ENDOCRINE_NEOPLASIA_TYPE_2 = 'multiple endocrine neoplasia type 2',
  MUTYH_ASSOCIATED_POLYPOSIS = 'MUTYH-associated polyposis',
  NEUROFIBROMATOSIS_TYPE_2 = 'neurofibromatosis type 2',
  OTC_DEFICIENCY = 'ornithine carbamoyltransferase deficiency',
  PARAGANGLIOMAS_1 = 'paragangliomas 1',
  PARAGANGLIOMAS_2 = 'paragangliomas 2',
  PARAGANGLIOMAS_3 = 'paragangliomas 3',
  PARAGANGLIOMAS_4 = 'paragangliomas 4',
  PEUTZ_JEGHERS_SYNDROME = 'Peutz-Jeghers syndrome',
  PTEN_HAMARTOMA_TUMOR_SYNDROME = 'PTEN hamartoma tumor syndrome',
  RETINOBLASTOMA = 'retinoblastoma',
  TUBEROUS_SCLEROSIS_COMPLEX = 'tuberous sclerosis complex',
  VASCULAR_EHLERS_DANLOS_SYNDROME = 'vascular Ehlers-Danlos syndrome',
  VON_HIPPEL_LINDAU_SYNDROME = 'Von Hippel-Lindau syndrome',
  WILMS_TUMOR = 'Wilms tumor',
  WILSON_DISEASE = 'Wilson disease',
}
export interface IVariant {
  id: number;
  // Alternate base(s); comma separated list of alterante non-reference alleles
  alt: string;
  // Chromosome name
  chrom: string;
  // Reference position where 1st base has position 1
  pos: number;
  // Reference base(s); must be one of A,C,G,T,N
  ref: string;
  // cnv_* variables will be null for non_CNVs
  cnv_copy_number: number;
  cnv_size: number;
  cnv_start_labels: string[];
  cnv_end_labels: string[];
  cnv_type: string;
  is_cnv: boolean;
  inserted_seq: string;
  reference_version: string;
  ensembl_url: string;
  spliceailookup_url: string;
  ucsc_url: string;
  gene: string;
  exon: string;
  reportable_disorders: Disorder[];
  // number of times the variant has been seen
  num_calls: {
    client: number;
  };
  nomenclatures: INomenclature[];
  genbank_ghgvs?: string;
  // list of source IDs
  sources: number[];
  is_potential_splice_variant: boolean;
  allele_fraction_summary: {
    median: number | null;
    low_fraction_count: number;
    non_low_fraction_count: number;
  };
}

enum ClassificationUnitTranscripts {
  ENSEMBL = 'Ensembl',
  REFSEQ = 'Refseq',
}

interface ITestType {
  test_type: string;
}
export interface IClassificationUnit {
  clinical_domain: string;
  effect: string;
  health_condition: string;
  id: number;
  latest_variant_classification: number;
  nomenclatures: INomenclature[];
  // transcripts are in the following format:
  // [['Ensembl', ensembl_transcript], ['Refseq', refseq_transcript]]
  // TODO: get_transcripts() API Refactor [UV-2833]
  transcripts: [ClassificationUnitTranscripts, string][];
  variant: IVariant;
  test_types: ITestType[];
}

export interface ISample {
  id: number;
  kit_barcode: string;
  report_id?: number;
  allele_fraction: number;
}

interface IReview {
  reviewer: {
    first_name: string;
  };
  notes: string;
  id: number;
  notes_from_requester: string;
}

// most states come from VariantClassification's FSM
// rereview is not a true state on the FSM, but used like it is on the frontend
export enum VariantClassificationState {
  NEW = 'new',
  IN_PROGRESS = 'in_progress',
  INTERPRETED = 'interpreted',
  REVIEWED = 'reviewed',
  REREVIEW = 'rereview',
}

// entire Variant Classification object
export interface IVariantClassification {
  id: number;
  classification: IClassification;
  classification_criteria: IClassificationCriteria[];
  classification_unit: IClassificationUnit;
  disorder: Disorder | null;
  state: VariantClassificationState;
  samples: ISample[];
  reviewed_at: string;
  // Notes shared among scientists and medical geneticists who approve classification
  classification_notes: string;
  reviews: IReview[];
  // Note shown on patient reports
  report_notes: string;
  // Notes shared among scientists internally
  research_notes: string;
  interpreter_name: string;
  reviewer_name: string;
  // Notes that explain the VUS-P (variants that are uncertain significance, likely pathogenic or pathogenic)
  // classification. on patient reports. Note overrides criteria description in classification of variant
  vusp_notes: string;
  send_for_external_review: boolean;
  last_reviewed_at: string | null;
}

// SendForReviewDialog uses the variant ID to send back interpreted = true
export interface ISendForReviewPayload {
  id: number;
  // TODO: look into API logic to make this field `is_interpreted` instead
  interpreted: boolean;
}

export interface IReleaseClassificationPayload {
  id: number;
  // TODO: look into API logic to rename these boolean fields [UV-3172]
  reviewed: boolean;
  send_for_external_review: boolean;
  notes_from_requester: string;
}

export interface IAddNotePayload {
  id: number;
  research_notes?: string;
  classification_notes?: string;
  report_notes?: string;
}

// report for anonymous report data used in VariantClassification details
export interface IReport {
  id: string; // report_id
  state: string;
  kit_order: IKitOrder;
  user: IReportUser;
  require_health_history_collection: boolean;
  sample_sequence_run: ISampleSequenceRun;
  sample: ISample;
}

interface IKitOrder {
  ready_at_lab?: string;
}

interface IReportUser {
  gender: string;
  age: number;
  health_profile: Record<string, any> | null;
}

interface ISampleSequenceRun {
  id: number;
  sample_run_variants: ISampleRunVariant[];
  // Lives on SampleSequenceRun via the `triggered_variant_confirmations`
  // related-name; populated by SampleSequenceRunSimpleSerializer.
  triggered_variant_confirmations?: ITriggeredVariantConfirmation[];
}

interface ISampleRunVariantValidation {
  confirmation_status: string;
}

interface IVariantCallData {
  allele_fraction: number;
  call_data: {
    /*
     * Genotype:
     * string encoded as allele values separated by "/" or "|"
     */
    GT: string;
    /*
     * Allele depth:
     * first element will be the number of reads that have the reference base
     * second element will be the number of reads that have an alternate base
     */
    AD: string[];
  };
}

// Mirrors VariantConfirmationSimpleSerializer; kept narrow to avoid coupling
// the reports page to the full VariantConfirmation type.
export interface ITriggeredVariantConfirmation {
  id: number;
  variant_id: number;
  is_present: boolean | null;
  state: string;
}

export interface ISampleRunVariant {
  id: number;
  created_at: string;
  kit_barcode: string;
  igv_link: string;

  variant_id: number;
  validation?: ISampleRunVariantValidation;
  variant_call_data: IVariantCallData;
}

/**
 * Source types come from list of source types that have not been
 * explicitly excluded in the ExternalInfoViewSet ([UV-1900])
 * and have a parser implemented
 */
enum ExternalPopulationInfoSource {
  COLOR = 'Color',
  DBSNP = 'dbSNP',
  GNOMAD = 'gnomAD',
  VARSOME = 'VarSome',
}

export enum ExternalInterpretationInfoSource {
  CLIN_VAR = 'Clinvar',
  UMD = 'UMD',
}

export interface IExternalInfo {
  source: string;
  url?: string;
}

export interface IExternalPopulationInfo extends IExternalInfo {
  source: ExternalPopulationInfoSource;
  entries: {
    populations: {
      name: string;
      // allele frequencies
      af: number;
      alt_count?: number;
      ref_count?: number;
      total_count?: number;
      homozygote_count?: number;
    }[];
    // dp stands for read depth; only gnomAD includes this field
    dp?: number;
    grpmax_faf_value?: number | null;
    grpmax_faf_population?: string;
    url: string;
  }[];
}

export interface IExternalInterpretationEntry {
  accession_number: string;
  classification: string;
  condition: string;
  depositors: {
    accession_number: string;
    name: string;
    classification: string;
    notes?: string;
    references?: {
      name: string;
      url: string;
    }[];
    updated_at: string;
  }[];
  notes: string;
  references: {
    name: string;
    url: string;
  }[];
  review_status: string;
  url: string;
}

export interface IExternalInterpretationInfo extends IExternalInfo {
  source: ExternalInterpretationInfoSource;
  url: string;
  entries: IExternalInterpretationEntry[];
}

export interface IExternalInfoResponse {
  results: (IExternalInfo | IExternalPopulationInfo | IExternalInterpretationInfo)[];
  search_query: string | null;
  clinvar_search_query: string;
}

export interface ISourceType {
  id: number;
  name: string;
  sub_type: string;
}

export interface ISource {
  id: number;
  external_id: string;
  type: number;
}

export interface ICreateSourcePayload {
  external_id: string;
  // equivalent to the source type ID
  type: number;
}

export interface IVariantClassificationListParams {
  state: string;
  clinicalDomain: string;
  variants: string;
  classification: string;
  kitBarcode: string;
  batchId: string;
  nomenclature: string;
  gene: string;
  exon: string;
  vepConsequence: string;
  readyForReview: string;
  orderBy: string;
  orderDirection: string;
}

export interface IVariantClassificationListItem {
  id: number;
  created_at: string;
  interpreted_at: string;
  updated_at: string;
  last_reviewed_at: string;
  reviewed_at: string;
  classification_unit: IClassificationUnit;
  classification: string;
  state: VariantClassificationState;
  ready_for_review: string;
}

export interface IAddVariantPayload {
  chrom: string;
  pos: number;
  ref: string;
  alt: string;
  tag: string;
}

export interface IClinvarDiscrepancy {
  name: string;
  classification: string;
}

export interface IReclassifyVariantPayload {
  classification_unit_id: number;
}

export interface ICreateClassificationReviewPayload {
  variant_classification: IVariantClassification;
  // is_internal is true when creating an internal classification review
  // is_internal is false when creating an external classification review
  is_internal: boolean;
}

export interface IVariantLog {
  event_type: string;
  event_time: string;
  classification: {
    short_name: string;
  } | null;
  classification_review: {
    id: number;
  } | null;
  triggered_by: {
    first_name: string;
  } | null;
  variant_classification: {
    id: number;
  } | null;
}

export interface IHistoricalNomenclature {
  history_date: string;
  previous_name: string | null;
  current_name: string | null;
  history_user: string | null;
}

export type VepConsequence = string;

export type VepVariantClass = string;

export interface IGenomicVariantDict {
  genome_reference_version: string;
  chromosome: string;
  position: number;
  reference: string;
  alternate: string;
}

export interface ISpliceAISpliceTypeSummary {
  score: number;
  relative_position: number;
}

export interface ISpliceAISummary {
  acceptor_gain: ISpliceAISpliceTypeSummary | null;
  acceptor_loss: ISpliceAISpliceTypeSummary | null;
  donor_gain: ISpliceAISpliceTypeSummary | null;
  donor_loss: ISpliceAISpliceTypeSummary | null;
}

export interface IVariantPredictionSummary {
  consequence: VepConsequence[];
  variant_type: VepVariantClass;
  alternative_variant_representations: IGenomicVariantDict[];
  gene: string | null;
  transcript: string | null;
  exon: string | null;
  intron: string | null;
  ccds: string | null;
  coding_strand: number;
  revel: number | null;
  alphamissense: number | null;
  bayesdel: number | null;
  spliceai_summary: ISpliceAISummary | null;
}

export type IPredictionSummaryResponse = IVariantPredictionSummary[];
